import React, { useState } from "react";
import { StyleSheet } from "react-native";
import { colors, layout } from "@/constants";
import Interactive from "./base/Interactive";
import Text from "./base/Text";
import ItemContextMenu from "./base/ItemContextMenu";
import ConfirmDeleteDialog from "./base/ConfirmDeleteDialog";

/**
 * Props interface for the DrawerSession component
 */
interface DrawerSessionProps {
  /** The session date text to display */
  title: string;
  /** Whether this session is currently selected */
  isSelected?: boolean;
  /** Callback function triggered when the session is pressed */
  onPress: () => void;
  /** Callback function triggered when the session is deleted */
  onDelete?: () => void;
  /** Accessibility label for screen readers */
  accessibilityLabel?: string;
}

/**
 * A session item component for the drawer.
 * The whole row is a single padded, rounded pressable. The optional context
 * menu is nested inside and stops event propagation so tapping it does not
 * also select the session.
 *
 * @param props - The component props
 * @returns A drawer list item for a session
 */
export default function DrawerSession({
  title,
  isSelected = false,
  onPress,
  onDelete,
  accessibilityLabel
}: DrawerSessionProps) {
  const [confirmVisible, setConfirmVisible] = useState(false);

  return (
    <>
      <Interactive
        style={styles.row}
        onPress={onPress}
        selected={isSelected}
        accessibilityLabel={accessibilityLabel || title}
        // Render as a <div>, not <button>, since the row contains a nested
        // button (ItemContextMenu). Nested <button> inside <button> is
        // invalid HTML and triggers a React dev-mode console error.
        accessibilityRole="menuitem"
      >
        <Text size="small" style={styles.title} numberOfLines={1}>
          {title}
        </Text>
        {onDelete && (
          <ItemContextMenu
            items={[
              {
                icon: "delete",
                label: "Delete",
                variant: "danger",
                onPress: () => setConfirmVisible(true)
              }
            ]}
          />
        )}
      </Interactive>

      <ConfirmDeleteDialog
        visible={confirmVisible}
        title="Delete session?"
        message="This will permanently delete this session and its messages. Facts and patterns will not be affected."
        onConfirm={() => {
          setConfirmVisible(false);
          onDelete?.();
        }}
        onCancel={() => setConfirmVisible(false)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: layout.space.xs,
    paddingVertical: layout.space.sm,
    paddingLeft: layout.space.md,
    borderRadius: layout.borderRadius.medium
  },
  title: {
    flex: 1,
    color: colors.onSurface
  }
});
